import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useContext, useCallback, useEffect, useMemo, createElement } from 'react';
import { TextField, Box, Typography, Select, MenuItem } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { IconContext, useApi, API } from '@plumeuk/shapeshift-identity';
import 'x2js';

const useStyles$3 = makeStyles()(() => ({
    timestampBadge: {},
    container: {
        backgroundColor: "#1F1F24",
        borderRadius: "20px",
        fontSize: "12px",
        fontWeight: "700",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "60px",
        height: "22px"
    }
}));
const TimestampBadge = ({ label, ...props }) => {
    const { classes, cx } = useStyles$3();
    return (jsx("span", { className: cx([classes.container, classes.timestampBadge]), ...props, children: label }));
};

const useStyles$2 = makeStyles()(() => ({
    input: {
        width: "100%",
        border: "1px solid #d8d8d8",
        borderRadius: "6px",
        padding: "10px 16px",
        boxSizing: "border-box",
        "& textarea": {
            paddingTop: "7px"
        }
    },
    icon: {
        color: "#d8d8d8",
        cursor: "pointer"
    },
    badge: {
        backgroundColor: "#1F1F24"
    },
    timeStampInputAdornment: {
        height: "100%",
        paddingRight: "10px",
        "& span": {
            marginTop: "5px"
        }
    },
    sendButtonInputAdornment: {
        height: "100%",
        paddingLeft: "10px"
    }
}));
const NoteInput = ({ postNote, placeholder = "Type your note", disableTimeStamp, className, content = "", videoRef, ...props }) => {
    const { classes, cx } = useStyles$2();
    const [note, setNote] = useState("");
    const [videoTime, setVideoTime] = useState(0);
    const { icon } = useContext(IconContext);
    const formatTimestamp = (timestamp) => {
        if (timestamp === 0) {
            return "00:00:00";
        }
        const hours = Math.floor(timestamp / 3600);
        const minutes = Math.floor((timestamp % 3600) / 60);
        const seconds = Math.floor(timestamp % 60);
        const formattedHours = String(hours).padStart(2, "0");
        const formattedMinutes = String(minutes).padStart(2, "0");
        const formattedSeconds = String(seconds).padStart(2, "0");
        return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    };
    const getVideoTimestamp = useCallback(() => {
        if (videoRef?.current?.react)
            return videoRef?.current?.react?.getCurrentTime();
        if (videoRef?.current?.mux)
            return videoRef?.current?.mux?.currentTime;
        if (videoRef?.current?.brightcove?.player?.currentTime)
            return videoRef.current.brightcove.player.currentTime();
        return undefined;
    }, [videoRef]);
    useEffect(() => {
        const interval = setInterval(() => {
            const videoTimestamp = getVideoTimestamp();
            if (videoTimestamp && videoTimestamp !== 0)
                setVideoTime(videoTimestamp);
        }, 100);
        return () => {
            clearInterval(interval);
        };
    }, [videoRef, getVideoTimestamp]);
    useEffect(() => {
        setNote(content);
    }, [content]);
    const handleChange = useCallback((event) => {
        setNote(event.target.value);
    }, [setNote]);
    const handleSubmit = useCallback(() => {
        if (!note)
            return;
        const videoTimestamp = getVideoTimestamp();
        const noteToPost = {
            content: note
        };
        if (videoTimestamp && videoTimestamp !== 0)
            noteToPost.videoTimestamp = videoTimestamp;
        postNote(noteToPost);
        setNote("");
    }, [note, setNote, postNote]);
    //TODO 
    //Timestamp hardcoded and label hidden
    //https://plume.eu.teamwork.com/app/tasks/20002785
    return (jsx(TextField, { className: cx(classes.input, className), placeholder: placeholder, "data-test-id": "note-input", variant: "standard", multiline: true, InputProps: {
            disableUnderline: true,
            autoCapitalize: "none",
            autoComplete: "off",
            startAdornment: ((disableTimeStamp !== true && videoRef?.current) && jsx(Box, { className: classes.timeStampInputAdornment, children: jsx(TimestampBadge, { label: formatTimestamp(videoTime) }) })),
            endAdornment: (jsx(Box, { className: classes.sendButtonInputAdornment, children: icon("sendOutlinedIcon", {
                    "data-test-id": "note-input-submit-btn",
                    className: classes.icon,
                    onClick: handleSubmit
                }) }))
        }, value: note, onChange: handleChange, ...props }));
};

const replaceAll = (str, find, replace) => {
    // Escape special characters in `find` using a regular expression
    const escapedFind = find.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // Create a regular expression with global flag to replace all occurrences
    const regex = new RegExp(escapedFind, "g");
    // Use the replace method with the regular expression
    return (str ?? "").replace(regex, replace);
};

const useStyles$1 = makeStyles()(() => ({
    container: {
        display: "flex",
        flexDirection: "row",
        marginTop: "24px"
    },
    title: {
        fontSize: "13px",
        fontWeight: "700",
        color: "#2A2A2F"
    },
    contentContainer: {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        paddingLeft: "16px"
    },
    note: {
        fontSize: "16px",
        fontWeight: "400",
        color: "#2A2A2F",
        marginTop: "6px"
    },
    timestampContainer: {
        width: "60px"
    },
    ctaContainer: {
        display: "flex",
        flexDirection: "row",
        marginTop: "6px"
    },
    ctaButton: {
        marginRight: "8px",
        color: "#A8A8A8",
        fontSize: "13px",
        fontWeight: "400",
        cursor: "pointer"
    }
}));
//TODO 
//Timestamp container hidden to remove spacing as timestamps not handled
//review when doing following ticket. 
//https://plume.eu.teamwork.com/app/tasks/20002785
const NoteCard = ({ forceIntent, videoTimestamp, title, note, editNote, deleteNote, className, ...props }) => {
    const [isEditing, setIsEditing] = useState(false);
    const handleToggleEditing = () => setIsEditing(state => !state);
    const handleUpdate = (note) => {
        handleToggleEditing();
        editNote(note.content);
    };
    const formatTimestamp = (timestamp) => {
        if (timestamp === 0) {
            return "00:00:00";
        }
        const hours = Math.floor(timestamp / 3600);
        const minutes = Math.floor((timestamp % 3600) / 60);
        const seconds = Math.floor(timestamp % 60);
        const formattedHours = String(hours).padStart(2, "0");
        const formattedMinutes = String(minutes).padStart(2, "0");
        const formattedSeconds = String(seconds).padStart(2, "0");
        return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    };
    const { classes, cx } = useStyles$1();
    const dataTestId = `note-card-${replaceAll(title.toLocaleLowerCase().substring(20), " ", "")}`;
    return (jsxs(Box, { className: cx(classes.container, className), ...props, children: [(videoTimestamp || forceIntent) &&
                jsx(Box, { className: classes.timestampContainer, children: videoTimestamp && jsx(TimestampBadge, { label: formatTimestamp(videoTimestamp) }) }), jsxs(Box, { className: classes.contentContainer, children: [jsx(Typography, { className: classes.title, children: title }), isEditing && jsx(NoteInput, { disableTimeStamp: true, content: note, postNote: handleUpdate }), !isEditing && jsx(Typography, { className: classes.note, children: note }), jsxs(Box, { className: classes.ctaContainer, children: [jsx(Typography, { "data-test-id": `${dataTestId}-${isEditing ? "cancel" : "edit"}-btn`, className: classes.ctaButton, onClick: handleToggleEditing, children: isEditing ? "Cancel" : "Edit" }), jsx(Typography, { "data-test-id": `${dataTestId}-delete-btn`, className: classes.ctaButton, onClick: deleteNote, children: "Delete" })] })] })] }));
};

/* eslint-disable no-console */
console.log.bind(null) ;
console.warn.bind(null) ;
console.error.bind(null) ;

const useNotes = (courseSlug, moduleType, moduleSlug, data) => {
    const [apiResult, getData, setNotes] = useApi((data || !courseSlug || !moduleSlug || !moduleType) ? null : API.notes(courseSlug, moduleType, moduleSlug), data);
    const getNotes = (courseSlugOverride, typeOverride, moduleSlugOverride, configOverride) => {
        const _type = typeOverride ?? moduleType;
        const _moduleSlug = moduleSlugOverride ?? moduleSlug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_courseSlug || !_moduleSlug || !_type)
            return;
        const url = API.notes(_courseSlug, _type, _moduleSlug);
        getData(configOverride ? { ...configOverride, url } : url);
    };
    useEffect(() => data && setNotes(data), [data]);
    return { notes: apiResult.data, apiResult, getNotes, setNotes };
};

const useNotePoster = () => {
    const [apiResult, getData] = useApi(null);
    const postNote = (note, courseSlug, moduleType, moduleSlug, config) => getData({
        ...(config ?? {}),
        data: note,
        url: API.notes(courseSlug, moduleType, moduleSlug),
        method: "POST"
    });
    const editNote = (id, content, config) => getData({
        ...(config ?? {}),
        data: { content },
        url: API.noteModify(id),
        method: "PATCH"
    });
    const deleteNote = (id, _content, config) => getData({
        ...(config ?? {}),
        url: API.noteModify(id),
        method: "DELETE"
    });
    return { response: apiResult.data, apiResult, postNote, editNote, deleteNote };
};

var SORT_BY;
(function (SORT_BY) {
    SORT_BY[SORT_BY["OLDEST"] = 0] = "OLDEST";
    SORT_BY[SORT_BY["LATEST"] = 1] = "LATEST";
})(SORT_BY || (SORT_BY = {}));
const useStyles = makeStyles()((theme) => ({
    filtersContainer: {
        display: "flex",
        flexDirection: "row",
        maxHeight: "60px"
    },
    filter: {
        width: "200px",
        backgroundColor: "white",
        color: "#2A2A2F !important"
    },
    recentFilter: {},
    inputContainer: {
        display: "flex",
        gap: "12px",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column"
        }
    }
}));
const NotesSection = ({ data, courseSlug, moduleSlug, moduleType, actionOverridePostNote, actionOverrideEditNote, actionOverrideDeleteNote, videoRef, disableTimeStamp, ...props }) => {
    const [sortBy, setSortBy] = useState(SORT_BY.LATEST);
    const { notes, getNotes } = useNotes(courseSlug, moduleType, moduleSlug, data);
    const { postNote, editNote, deleteNote, apiResult: postNoteResponse } = useNotePoster();
    useEffect(() => {
        if (!postNoteResponse.isLoading) {
            getNotes(courseSlug, moduleType, moduleSlug, { cache: false });
        }
    }, [postNoteResponse, courseSlug, moduleSlug, moduleType]);
    const handlePostNote = useCallback((note) => {
        if (!actionOverridePostNote && courseSlug && moduleSlug) {
            postNote(note, courseSlug, moduleType, moduleSlug);
        }
        else if (actionOverridePostNote) {
            actionOverridePostNote(note);
        }
    }, [courseSlug, moduleType, moduleSlug]);
    const handleEditNote = useCallback((id, content) => {
        if (!actionOverrideEditNote && content) {
            editNote(id, content);
        }
        else if (actionOverrideEditNote) {
            actionOverrideEditNote(id, content);
        }
    }, []);
    const handleDeleteNote = useCallback((id) => {
        if (!actionOverrideDeleteNote) {
            deleteNote(id);
        }
        else if (actionOverrideDeleteNote) {
            actionOverrideDeleteNote(id);
        }
    }, []);
    const sortedNotes = useMemo(() => {
        return notes?.sort((a, b) => {
            const dateA = new Date(a.createdAt).getTime();
            const dateB = new Date(b.createdAt).getTime();
            return sortBy === SORT_BY.OLDEST ? dateA - dateB : dateB - dateA;
        });
    }, [notes, sortBy]);
    const { classes, cx } = useStyles();
    return (createElement(Box, { ...props, key: moduleSlug + "" + moduleType },
        jsxs(Box, { className: classes.inputContainer, children: [jsx(NoteInput, { disableTimeStamp: disableTimeStamp, postNote: handlePostNote, videoRef: videoRef }), jsx(Box, { className: classes.filtersContainer, children: jsxs(Select, { value: sortBy, className: cx(classes.filter, classes.recentFilter), variant: "outlined", onChange: value => setSortBy(value.target.value), children: [jsx(MenuItem, { value: SORT_BY.LATEST, children: "Most recent" }), jsx(MenuItem, { value: SORT_BY.OLDEST, children: "Oldest first" })] }) })] }),
        sortedNotes?.map(note => jsx(NoteCard, { forceIntent: !!sortedNotes.find(e => e.videoTimestamp), "data-test-id": `note-card-${note.id}`, title: note.course, note: note.content, videoTimestamp: note.videoTimestamp, editNote: (content) => handleEditNote(note.id, content), deleteNote: () => handleDeleteNote(note.id) }, note.id))));
};

export { NoteCard, NoteInput, NotesSection, TimestampBadge };
